/**
 * Dispatcher: maps a ConditionNode's type to the correct renderer component.
 * Matches ~/code/ui condition-editor-v2 render-node.gts
 *
 * ConditionGroup is co-located here because the two components are mutually
 * recursive — group renders nodes, nodes can be groups — and living in the
 * same module avoids a circular import.
 */

import { Badge, ConditionViewerGroup, Text } from '@customerio/pluma-components/react';
import type { ConditionNode as ConditionNodeType, GroupNode } from '../types';
import { AttributeCondition } from './renderers/attribute-condition';
import { EventCondition } from './renderers/event-condition';
import { DeviceCondition } from './renderers/device-condition';
import { PageviewCondition } from './renderers/pageview-condition';
import { ScreenviewCondition } from './renderers/screenview-condition';
import { FormCondition } from './renderers/form-condition';
import { InboundCondition } from './renderers/inbound-condition';
import { OptOutCondition } from './renderers/opt-out-condition';
import { MessageCondition } from './renderers/message-condition';
import { SegmentCondition } from './renderers/segment-condition';
import { RelationshipCondition } from './renderers/relationship-condition';
import { UnknownCondition } from './renderers/unknown-condition';

// =============================================================================
// ConditionGroup — renders a group node (and/or) with recursive children
// =============================================================================

interface ConditionGroupProps {
  node: GroupNode;
  depth?: number;
  workspaceId: number;
}

export function ConditionGroup({ node, depth = 0, workspaceId }: ConditionGroupProps) {
  const { children, operator } = node;

  if (children.length === 0) {
    return (
      <Text size="sm" color="subtle">
        No conditions
      </Text>
    );
  }

  // Single child — render directly without group wrapper
  if (children.length === 1) {
    return <ConditionNodeRenderer node={children[0]} depth={depth} workspaceId={workspaceId} />;
  }

  const groupLabel = operator === 'and' ? 'all' : 'any';

  return (
    <ConditionViewerGroup
      label={
        <>
          Matches <Badge>{groupLabel}</Badge> of {children.length} conditions
        </>
      }
      isNested={depth > 0}
      isCollapsible={depth > 0}
    >
      {children.map((child, index) => (
        <ConditionNodeRenderer
          key={`${child.type}-${index}`}
          node={child}
          depth={depth + 1}
          workspaceId={workspaceId}
        />
      ))}
    </ConditionViewerGroup>
  );
}

// =============================================================================
// ConditionNodeRenderer — dispatches a node to the correct renderer
// =============================================================================

interface ConditionNodeProps {
  node: ConditionNodeType;
  depth?: number;
  workspaceId: number;
}

export function ConditionNodeRenderer({ node, depth = 0, workspaceId }: ConditionNodeProps) {
  switch (node.type) {
    case 'group':
      return <ConditionGroup node={node} depth={depth} workspaceId={workspaceId} />;
    case 'attribute':
      return <AttributeCondition node={node} />;
    case 'event':
      return <EventCondition node={node} />;
    case 'segment':
      return <SegmentCondition node={node} workspaceId={workspaceId} renderCondition={ConditionNodeRenderer} />;
    case 'device':
      return <DeviceCondition node={node} />;
    case 'pageview':
      return <PageviewCondition node={node} />;
    case 'screenview':
      return <ScreenviewCondition node={node} />;
    case 'form':
      return <FormCondition node={node} />;
    case 'message':
      return <MessageCondition node={node} workspaceId={workspaceId} />;
    case 'inbound':
      return <InboundCondition node={node} />;
    case 'opt_out':
      return <OptOutCondition node={node} />;
    case 'relationship':
      return <RelationshipCondition node={node} />;
    case 'unknown':
      return <UnknownCondition node={node} />;
    default: {
      const _exhaustive: never = node;
      return <UnknownCondition node={{ type: 'unknown', raw: _exhaustive }} />;
    }
  }
}
